import React from "react";
import Layout, { PageHeader, Surface } from "@/components/Layout";

const Block = ({ children }) => (
  <pre className="bg-zinc-950 text-emerald-200 font-mono text-[11px] p-4 rounded-sm overflow-x-auto">{children}</pre>
);

const SECTIONS = [
  {
    id: "auth",
    title: "Authentication",
    body: (
      <>
        <p className="text-sm text-zinc-600 mb-3">
          Every Proxy API request must include your API key in the <code className="font-mono text-xs bg-zinc-100 px-1 rounded-sm">X-API-Key</code> header.
          Keys are issued by Blonix Operations and rotated by Super Admin.
        </p>
        <Block>{`X-API-Key: bk_live_xxxxxxxxxxxxxxxxxxxxxxxx
Idempotency-Key: <uuid>   # for POST endpoints`}</Block>
      </>
    ),
  },
  {
    id: "users",
    title: "Users (Cardholders)",
    body: (
      <Block>{`POST   /api/v1/users
GET    /api/v1/users
GET    /api/v1/users/:id

# Body for create:
{
  "external_user_id": "u_3xT9",
  "first_name": "Ada",
  "last_name": "Lovelace",
  "email": "ada@acme.io",
  "phone": "+15551234567",
  "metadata": {}
}`}</Block>
    ),
  },
  {
    id: "cards",
    title: "Cards",
    body: (
      <>
        <p className="text-sm text-zinc-600 mb-3">
          Issue virtual or physical cards, load balance, query issuer bills, and manage card lifecycle.
          Use <code className="font-mono text-xs bg-zinc-100 px-1 rounded-sm">POST /api/v1/cards/apply</code> for
          full Tevau apply flow (virtual + physical shipping). Legacy <code className="font-mono text-xs bg-zinc-100 px-1 rounded-sm">POST /api/v1/cards/create</code> remains for sandbox-style issuance.
        </p>
        <Block>{`# List & read
GET    /api/v1/cards                  ?user_id=&page=&limit=   (merchant: all cards)
POST   /api/v1/cards/list            { user_id }  ?page=&limit=  → Tevau card list (total, list, pages)
POST   /api/v1/cards/get              { card_id }
POST   /api/v1/cards/transactions     { card_id }  ?limit=
POST   /api/v1/cards/recharge/history { card_id? } ?page=&limit=
GET    /api/v1/cards/products?user_id=<optional>

# Issue & bind
POST   /api/v1/cards/create        { user_id, card_type_id, initial_load? }
POST   /api/v1/cards/apply         { user_id, card_type_id, dial_code, phone_number, email?,
                                    address, city, country_area, post_code,
                                    first_name?, last_name?, province?, initial_load? }
POST   /api/v1/cards/bind          { user_id, active_code, card_number, email,
                                    dial_code, phone_number, address, city, country_area, post_code }

# Lifecycle
POST   /api/v1/cards/freeze        { card_id, reason? }
POST   /api/v1/cards/unfreeze      { card_id }
POST   /api/v1/cards/terminate     { card_id, reason? }
POST   /api/v1/cards/detail        { card_id }
POST   /api/v1/cards/activate      { card_id, active_code }
POST   /api/v1/cards/order         { card_id } | { order_no }

# Balance & sensitive
POST   /api/v1/cards/recharge      { card_id, amount }
POST   /api/v1/cards/recharge/fee  { card_id, amount }
POST   /api/v1/cards/pan-html      { card_id }
POST   /api/v1/cards/pin           { card_id }
POST   /api/v1/cards/limits        { card_id }

# Contact & fees
POST   /api/v1/cards/email         { card_id, email }
POST   /api/v1/cards/phone         { card_id, dial_code, phone_number }
POST   /api/v1/cards/fees/set      { card_id, card_fee_config_list: [{ charge_item, fixed_amount, percentage }] }
POST   /api/v1/cards/fees/query    { card_id }
POST   /api/v1/cards/3ds           { auth_id, approve: true|false }

# Bills (issuer transaction history)
POST   /api/v1/cards/bills         { card_id, page?, limit?, create_time_start, create_time_end }
POST   /api/v1/cards/bills/detail  { card_id, order_id }

# Card object
{
  "id": "card_...",
  "type": "virtual",
  "status": "active",
  "masked_pan": "**** **** **** 4242",
  "expiry": "07/29",
  "balance": 0.00,
  "currency": "USD"
}`}</Block>
      </>
    ),
  },
  {
    id: "deposits",
    title: "Deposits",
    body: (
      <Block>{`POST   /api/v1/deposits/address    { card_id, currency: "USDC" }
POST   /api/v1/deposits/notify     { card_id, amount, currency, tx_hash }
GET    /api/v1/deposits`}</Block>
    ),
  },
  {
    id: "tx",
    title: "Transactions",
    body: <Block>{`GET    /api/v1/transactions
GET    /api/v1/transactions/:id`}</Block>,
  },
  {
    id: "wh",
    title: "Webhooks",
    body: <Block>{`POST   /api/v1/webhooks/register   { url, events: ["card.*"] }
POST   /api/v1/webhooks/test       { event: "ping" }`}</Block>,
  },
  {
    id: "errors",
    title: "Errors",
    body: (
      <>
        <p className="text-sm text-zinc-600 mb-3">All errors follow a normalized envelope:</p>
        <Block>{`HTTP 4xx/5xx
{
  "detail": "Insufficient merchant balance for issuance fee"
}`}</Block>
      </>
    ),
  },
];

export default function ApiDocs() {
  return (
    <Layout>
      <div className="p-6 lg:p-10" data-testid="api-docs-page">
        <PageHeader
          title="API Reference"
          subtitle="The merchant-facing Proxy API. Upstream issuer internals are abstracted away."
        />

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <aside className="lg:col-span-1 self-start lg:sticky lg:top-6">
            <Surface className="p-4">
              <div className="text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500 mb-2">On this page</div>
              <ul className="space-y-1.5">
                {SECTIONS.map((s) => (
                  <li key={s.id}>
                    <a className="text-sm text-zinc-600 hover:text-zinc-950 transition-colors duration-150" href={`#${s.id}`}>{s.title}</a>
                  </li>
                ))}
              </ul>
            </Surface>
          </aside>

          <div className="lg:col-span-3 space-y-6">
            {SECTIONS.map((s) => (
              <Surface key={s.id} className="p-6" testId={`api-section-${s.id}`}>
                <h2 id={s.id} className="font-display text-xl tracking-tight font-medium mb-3">{s.title}</h2>
                {s.body}
              </Surface>
            ))}

            <Surface className="p-6">
              <h2 className="font-display text-xl tracking-tight font-medium mb-3">Architecture</h2>
              <p className="text-sm text-zinc-600 mb-3">
                Blonix wraps the underlying card-issuing network behind a versioned Proxy API. Merchants
                see only normalised Blonix DTOs — no upstream IDs, URLs, or credentials are exposed.
              </p>
              <Block>{`Merchant App -> X-API-Key -> Blonix Proxy API
                                |--> Auth + Rate Limit + Idempotency
                                |--> Fee Engine (debit balance)
                                |--> Audit Logger
                                |--> Internal Issuer Adapter
                                       |--> Upstream Card-Issuing Network`}</Block>
            </Surface>
          </div>
        </div>
      </div>
    </Layout>
  );
}
